import type { OrderView } from '@seen/core/models/orderView'
import { omit } from 'lodash-es'
import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'
import type { Filter } from '#src/components/filter/Filter.tsx'
import type { OrderListGrouping } from '#src/components/order/OrdersList.tsx'

interface OrderViewDisplay {
  showLabels: boolean
  showNextAppointment: boolean
  showNextTask: boolean
  showLastNote: boolean
  showPerformer: boolean
  showRequester: boolean
  showDueDate: boolean
  showDocuments: boolean
}

const DEFAULT_DISPLAY: OrderViewDisplay = {
  showLabels: true,
  showNextAppointment: true,
  showNextTask: true,
  showLastNote: true,
  showPerformer: true,
  showRequester: true,
  showDueDate: true,
  showDocuments: true,
}

export type OrdersPageView =
  | 'all'
  | 'active'
  | 'unsigned'
  | 'myOrders'
  | OrderView['referenceString']

const EMPTY_FILTER_VIEW: Filter[] = []

export const useOrdersPageStore = create(
  devtools(
    persist(
      combine(
        {
          view: 'myOrders' as OrdersPageView,
          filtersByView: {} as Record<string, Filter[]>,
          displayByView: {} as Record<
            string,
            Partial<OrderViewDisplay> | undefined
          >,
          sort: { field: 'hybrid', direction: 'asc' as 'asc' | 'desc' },
          grouping: undefined as OrderListGrouping,
          lastViewedOrderId: null as string | null,
        },
        (set) => ({
          updateCurrentFilters: (
            nextFilters: Filter[] | ((currentFilters: Filter[]) => Filter[])
          ) => {
            set((state) => ({
              filtersByView: {
                ...state.filtersByView,
                [state.view]:
                  typeof nextFilters === 'function'
                    ? nextFilters(
                        state.filtersByView[state.view] ?? EMPTY_FILTER_VIEW
                      )
                    : nextFilters,
              },
            }))
          },
          updateCurrentSort: (
            nextSort:
              | { field: string; direction: 'asc' | 'desc' }
              | ((currentSort: {
                  field: string
                  direction: 'asc' | 'desc'
                }) => { field: string; direction: 'asc' | 'desc' })
          ) => {
            set((state) => ({
              sort:
                typeof nextSort === 'function'
                  ? nextSort(state.sort)
                  : nextSort,
            }))
          },
          changeView: (nextView: OrdersPageView, defaultFilters: Filter[]) => {
            set((state) => {
              const shouldUpdateFilters = !state.filtersByView[nextView]
              return {
                view: nextView,
                filtersByView: shouldUpdateFilters
                  ? {
                      ...state.filtersByView,
                      [nextView]: defaultFilters,
                    }
                  : state.filtersByView,
              }
            })
          },
          setLastViewedOrderId: (id: string | null) =>
            set({ lastViewedOrderId: id }),
          unsetSort: () =>
            set({
              sort: { field: 'hybrid', direction: 'asc' as 'asc' | 'desc' },
            }),
          updateGrouping: (nextGrouping: OrderListGrouping) => {
            set({ grouping: nextGrouping })
          },
          unsetGrouping: () => set({ grouping: undefined }),
          setDisplay: ({
            display,
            forView,
          }: {
            display:
              | Partial<OrderViewDisplay>
              | ((
                  currentDisplay: Partial<OrderViewDisplay>
                ) => Partial<OrderViewDisplay>)
            forView: OrdersPageView
          }) => {
            set((state) => ({
              displayByView: {
                ...state.displayByView,
                [forView]:
                  typeof display === 'function'
                    ? display(state.displayByView[forView] ?? {})
                    : display,
              },
            }))
          },
          unsetDisplay: (view: OrdersPageView) => {
            set((state) => ({
              displayByView: omit(state.displayByView, view),
            }))
          },
        })
      ),
      {
        name: 'orders-page-store',
        partialize: (state) => ({
          view: state.view,
          filtersByView: state.filtersByView,
          displayByView: state.displayByView,
          sort: state.sort,
          grouping: state.grouping,
        }),
        version: 2,
      }
    )
  )
)

export { DEFAULT_DISPLAY }
export type { OrderViewDisplay }
